import { useEffect, type ReactNode } from 'react';
import { openStagingGateSignIn } from '@gearghost/api-schemas/stagingGate';
import { useStagingGateStatusQuery } from '@/redux/api/stagingGateApi';
import { isStagingGateEnabled } from '@/utils/env';
import LoadingSkeleton from '@common/feedback/LoadingSkeleton';

/**
 * The staging gate (docs/operations.md, Staging gate). On a staging build,
 * nothing under this renders until the staging backend says the gate
 * cookie is set. Off staging it renders its children straight away and
 * never asks.
 *
 * A failed status read sends the tab to staging's sign-in page, which a
 * production admin's passkey opens and which brings the tab back (#1319
 * "Staging gate: sign in with a production admin passkey"). Any failure,
 * not only the gate's own 401: with the backend unreachable, the sign-in
 * page is where the real error surfaces, and a blank page would say
 * nothing.
 */
export function StagingGate({ children }: { children: ReactNode }) {
  const enabled = isStagingGateEnabled();
  const { data, isError } = useStagingGateStatusQuery(undefined, { skip: !enabled });

  useEffect(() => {
    if (isError) openStagingGateSignIn(window.location);
  }, [isError]);

  if (!enabled || data) return children;
  return <LoadingSkeleton type='page' />;
}
